import { Component, OnInit } from '@angular/core';
import {} from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { finalize, takeUntil, tap } from 'rxjs/operators';

import { QueryStringTypesEnum } from 'src/app/configs/query-string-types';
import {
  CustomResultOfOutboxMessageNumbersWithPaginationDto,
  OutboxMessageNumbersWithPaginationDto,
  OutboxMessageNumbersWithPaginationItem,
} from 'src/app/core/api/models';
import { EventTypes } from 'src/app/core/constants/event-types';
import { DefaultSearchValueService } from 'src/app/core/services/default-search-value.service';
import { AppDeliveryStatus } from 'src/app/core/types/app-delivery-status.type';
import { DeliveryStatus } from '../../../../core/api/models/delivery-status';
import { GetEnumValuesDto } from '../../../../core/api/models/get-enum-values-dto';
import { SendStatus } from '../../../../core/api/models/send-status';
import { SmsAggregator } from '../../../../core/api/models/sms-aggregator';
import { OutboxMessageNumbersService } from '../../../../core/api/services/outbox-message-numbers.service';
import { FileService } from '../../../../core/services/file.service';
import { BaseIndexPageComponent } from '../../../shared/base/components/base-index-page.component';
import { CreateReceiversExcelDialogComponent } from '../create-receivers-excel/create-receivers-excel-dialog.component';
import { DeliveryStatusStats } from '../delivery-status-stats/delivery-status-stats.component';
import { EditSendReceiversDeliveryStatus } from '../edit-send-receivers-delivery-status/edit-send-receivers-delivery-status.component';
import { ExcelService } from './../../../../core/api/services';
import { AppDomService } from './../../../../core/services/dom.service';
import { ScheduledMessageIdsStorageService } from './../../../../core/services/scheduled-message-ids-storage.service';
import { IsSmartTypeService } from './is-smart-type.service';

@Component({
  selector: 'app-send-receivers',
  templateUrl: './send-receivers.component.html',
  styleUrls: ['./send-receivers.component.scss'],
  standalone: false,
})
export class SendReceiversComponent
  extends BaseIndexPageComponent
  implements OnInit
{
  //#region Properties

  pageName = 'sendQueue.receivers';

  scheduledMessageId: number;
  modelWithPagination$: Observable<OutboxMessageNumbersWithPaginationDto>;
  items$: Observable<Array<OutboxMessageNumbersWithPaginationItem>>;

  mobileNumbers: string[] = [];

  //enums
  get DeliveryStatus() {
    return DeliveryStatus;
  }
  get SendStatus() {
    return SendStatus;
  }
  get SmsAggregator() {
    return SmsAggregator;
  }

  DeliveryStatusEnumValues: GetEnumValuesDto[] = [];
  SendStatusEnumValues: GetEnumValuesDto[] = [];
  SmsAggregatorEnumValues: GetEnumValuesDto[] = [];

  //loadings
  downloadLoading: boolean = false;
  changeStatusSelectiveLoading: boolean = false;
  changeStatusAllLoading: boolean = false;
  changeStatusByFileLoading: boolean = false;
  isTooltipDisabled: boolean = true;
  isSmartType: boolean = false;

  subscription: Subscription;

  //#endregion

  constructor(
    protected currentService: OutboxMessageNumbersService,
    protected sanitizer: DomSanitizer,
    protected fileService: FileService,
    protected translate: TranslateService,
    protected excelFileService: ExcelService,
    protected scheduledMessageIdsStorageService: ScheduledMessageIdsStorageService,
    protected defaultSearchValueService: DefaultSearchValueService
  ) {
    super();
    this.scheduledMessageId =
      this.scheduledMessageIdsStorageService.scheduledMessageIds.pop();
    this.isSmartType = IsSmartTypeService.isSmart;
  }

  ngOnInit() {
    try {
      if (this.scheduledMessageId > 0) {
        this.location.go(
          window.location.pathname +
            '?' +
            QueryStringTypesEnum.ScheduledMessage_key +
            '=' +
            this.scheduledMessageId
        );
      } else {
        let params = this.routingState.parseQueryString();
        for (const [key, value] of Object.entries(params)) {
          if (key == QueryStringTypesEnum.ScheduledMessage_key) {
            this.scheduledMessageId = +value;
          }
        }
      }
      super.ngOnInit();

      this.pageConfig.loading = true;
      this.pageConfig.tableConfig.comparisonObjects = []; //clean stored search
      this.pageConfig.showDefaultBtns = false;
      this.pageConfig.tableConfig.showOperationColumn = false;
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.smsReport);
    }
  }

  getEnumsValues() {
    this.getEnumValuesApi('SmsAggregator')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((values) => (this.SmsAggregatorEnumValues = values));
    this.getEnumValuesApi('SendStatus')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((values) => (this.SendStatusEnumValues = values));
    this.getEnumValuesApi('DeliveryStatus')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((values) => (this.DeliveryStatusEnumValues = values));
  }

  configTableColumns() {
    this.tableVocabs = this.pageVocabs;

    this.tableColumns = [
      {
        title: ``,
        name: '',
        hasSearch: false,
        noSorting: true,
        isCheckBox: true,
      },
      // smsText
      { title: `common.smsText`, name: 'smsText', hasSearch: true },
      // smsParts
      {
        title: `common.smsParts`,
        name: 'smsParts',
        hasSearch: true,
        type: 'number',
      },
      // topNumber
      {
        title: `${this.tableVocabs}toNumber`,
        name: 'toNumber',
        hasSearch: true,
        dir: 'ltr',
      },
      // mobileOperator
      {
        title: `common.operator`,
        name: 'mobileOperator',
        hasSearch: true,
        type: 'enum',
        enumName: 'SmsAggregator',
      },
      // sendStatus
      {
        title: `${this.tableVocabs}sendStatus`,
        name: 'SendStatus',
        hasSearch: true,
        type: 'enum',
        enumName: 'SendStatus',
      },
      //deliveryStatus
      {
        title: `${this.tableVocabs}deliveryStatus`,
        name: 'DeliveryStatus',
        hasSearch: true,
        type: 'enum',
        enumName: 'DeliveryStatus',
      },
      //statusDateTime
      { title: `${this.tableVocabs}statusDateTime`, name: 'statusDateTime' },
      // {
      //   title: `${this.tableVocabs}sendDateTime`,
      //   name: 'sendDateTime',
      //   hasSearch: true,
      //   // dateDefaultValue: this.defaultSearchValueService.defaultDateValue,
      //   // isRequired: true,
      //   type: 'date',
      //   wrap: true,
      // },
    ];

    if (this.isSmartType)
      this.tableColumns = this.tableColumns.filter(
        (column) => column.name !== 'toNumber'
      );
  }

  setDefaultTableSort() {
    this.sortObject.push({ columnName: 'statusDateTime', sort: 1 });
  }

  //#region Api
  getBaseModelWithPaginationParams() {
    return {
      pageNumber:
        this.paginationModel.pageSize > this.paginationModel.totalCount
          ? 1
          : this.paginationModel.pageNumber,
      pageSize: this.paginationModel.pageSize,
      comparisonObjects: this.comparisonObjects.filter((item) => {
        // Check if the item has a comparison object with the field "id"
        return !item.comparisonObjects.some((obj) => obj.field === 'id');
      }),
      orders: this.sortObject,
    };
  }
  //Get All
  getPaginationApi(): Observable<CustomResultOfOutboxMessageNumbersWithPaginationDto> {
    try {
      if (
        this.pageConfig.tableConfig.paginationModel.pageSize >
        this.pageConfig.tableConfig.paginationModel.totalCount
      )
        this.pageConfig.tableConfig.paginationModel.pageNumber = 1;
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.smsReport);
    }

    return this.currentService.outboxMessageNumbersGetScheduledMessageNumbersWithPagination(
      {
        body: {
          ...this.getBaseModelWithPaginationParams(),
          scheduledMessageId: this.scheduledMessageId,
        },
      }
    );
  }

  updateDeliveryStatusByDeliveryStatusAndScheduledMessageIdGetApi(data) {
    if (this.changeStatusAllLoading) return;

    this.unSubs();
    // this.pageConfig.loading = true;
    this.changeStatusAllLoading = true;

    this.currentService
      .outboxMessageNumbersUpdateDeliveryStatusByDeliveryStatusAndScheduledMessageIdGet(
        {}
      )
      .pipe(
        this.basePipeOperators(),
        finalize(() => (this.changeStatusAllLoading = false))
      )
      .subscribe((res) => {
        if (res) this.openEditSendReceiversDeliveryStatus(data);
      });
  }

  updateOutBoxMessageNumbersDeliveryStatusGetApi(data) {
    if (this.changeStatusByFileLoading) return;

    this.unSubs();

    this.changeStatusByFileLoading = true;

    this.currentService
      .outboxMessageNumbersUpdateOutBoxMessageNumbersDeliveryStatusGet({})
      .pipe(
        this.basePipeOperators(),
        finalize(() => {
          this.changeStatusByFileLoading = false;
        })
      )
      .subscribe((res) => {
        if (res) this.openEditSendReceiversDeliveryStatus(data);
      });
  }

  updateSelectedOutBoxMessageNumbersDeliveryStatusGetApi(data) {
    if (this.changeStatusSelectiveLoading) return;

    this.unSubs();

    this.changeStatusSelectiveLoading = true;

    this.currentService
      .outboxMessageNumbersUpdateSelectedOutBoxMessageNumbersDeliveryStatusGet(
        {}
      )
      .pipe(
        this.basePipeOperators(),
        finalize(() => {
          this.changeStatusSelectiveLoading = false;
        })
      )
      .subscribe((res) => {
        if (res) this.openEditSendReceiversDeliveryStatus(data);
      });
  }

  //#endregion

  //#region download

  onDownloadReceiversGetApi() {
    if (this.downloadLoading) return;

    this.excelFileService
      .excelCreateReceiversExcelGet()
      .pipe(this.basePipeOperators())
      .subscribe((res) => {
        if (res) {
          let data = {
            title: `${this.pageVocabs}receiversExcel`,
            scheduledMessageId: this.scheduledMessageId,
            comparisonObjects: this.comparisonObjects,
            sortObject: this.sortObject,
          };

          const modalResult = this.dialogService.openModal({
            component: CreateReceiversExcelDialogComponent,
            data: data,
          });

          modalResult
            .afterClosed()
            .pipe(
              takeUntil(this.onDestroy$),
              tap(() => {
                AppDomService.removeModalScrollClassFromHtml();
              })
            )
            .subscribe();
        }
      });
  }

  onDownloadReceiversPostApi() {
    this.downloadLoading = true;

    this.excelFileService
      .excelCreateReceiversExcelPost({
        body: {
          title: 'outboxReceivers',
          scheduledMessageId: this.scheduledMessageId,
        },
      })
      .pipe(
        this.basePipeOperators(),
        finalize(() => (this.downloadLoading = false))
      )
      .subscribe((file) => {
        // UtilService.downloadFile(file.data);
      });
  }

  //#endregion

  //#region edit delivery status

  onEditDeliveryStatus(type: AppDeliveryStatus) {
    try {
      this.unSubs();

      let data;

      switch (type) {
        case 'selective':
          data = {
            scheduledMessageId: this.scheduledMessageId,
            receivers: this.mobileNumbers.join(),
            DeliveryStatusEnumValues: this.DeliveryStatusEnumValues,
            type: type,
          };
          this.updateSelectedOutBoxMessageNumbersDeliveryStatusGetApi(data);
          break;
        case 'byFile':
          data = {
            scheduledMessageId: this.scheduledMessageId,
            receivers: this.mobileNumbers.join(),
            DeliveryStatusEnumValues: this.DeliveryStatusEnumValues,
            type: type,
          };
          this.updateOutBoxMessageNumbersDeliveryStatusGetApi(data);
          break;
        case 'all':
          data = {
            scheduledMessageId: this.scheduledMessageId,
            DeliveryStatusEnumValues: this.DeliveryStatusEnumValues,
            type: type,
          };
          this.updateDeliveryStatusByDeliveryStatusAndScheduledMessageIdGetApi(
            data
          );
          break;
        default:
          break;
      }
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.smsReport);
    }
  }

  openEditSendReceiversDeliveryStatus(data) {
    const modalResult = this.dialogService.openModal({
      component: EditSendReceiversDeliveryStatus,
      data: data,
    });

    modalResult
      .afterClosed()
      .pipe(
        takeUntil(this.onDestroy$),
        tap(() => {
          AppDomService.removeModalScrollClassFromHtml();
        })
      )
      .subscribe((res) => {
        if (res) {
          this.mobileNumbers = [];
          this.getTableData();
          this.unCheckSelectAll();
          this.cd.detectChanges();
        }
      });
  }

  isSelectAll() {
    try {
      const checkBoxAllElement = document.querySelectorAll(
        `#${'receivers-' + this.scheduledMessageId} .select-all`
      );
      let checkboxes = document.querySelectorAll(
        `#${
          'receivers-' + this.scheduledMessageId
        } input[type="checkbox"]:checked`
      );
      //to check if 'select all' checkBox exists in checkboxes list
      let isExistsSelectAll = checkboxes[0].classList.contains('select-all');
      if (!isExistsSelectAll) {
        if (
          (this.paginationModel.totalCount < this.paginationModel.pageSize &&
            checkboxes.length == this.paginationModel.totalCount) ||
          checkboxes.length == this.paginationModel.pageSize
        ) {
          (checkBoxAllElement[0] as HTMLInputElement).checked = true;
        }
      }
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.smsReport);
    }
  }

  onAddToMobileNumbers(event, mobileNumber: string, element) {
    try {
      const checkBoxAllElement = document.querySelectorAll(
        `#${'receivers-' + this.scheduledMessageId} .select-all`
      )[0] as HTMLInputElement;

      let checked = event?.target?.checked;
      if (!mobileNumber) return;

      if (checked) {
        //add
        this.isSelectAll();
        if (this.mobileNumbers?.find((item) => item === mobileNumber)) return;
        this.mobileNumbers?.push(mobileNumber);
        (element as HTMLElement).classList.add('row-active');
      } else {
        //remove
        const selectedMobileNumberIndex = this.mobileNumbers?.findIndex(
          (item) => item === mobileNumber
        );
        if (selectedMobileNumberIndex != -1)
          this.mobileNumbers?.splice(selectedMobileNumberIndex, 1);
        (element as HTMLElement).classList.remove('tr-active');
        if (checkBoxAllElement.checked) checkBoxAllElement.checked = false;
      }
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.smsReport);
    }
  }

  unCheckSelectAll() {
    try {
      const checkBoxAllElement = document.querySelectorAll(
        `#${'receivers-' + this.scheduledMessageId} .select-all`
      )[0] as HTMLInputElement;
      if (checkBoxAllElement) checkBoxAllElement.checked = false;
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.smsReport);
    }
  }

  tooltipDisabled(mobileOperator: number) {
    if (mobileOperator == 1 || mobileOperator == 2 || mobileOperator == 3)
      this.isTooltipDisabled = false;
    else this.isTooltipDisabled = true;

    return this.isTooltipDisabled;
  }

  // checkbox
  isChecked(mobileNumber: string, element): boolean {
    try {
      if (this.mobileNumbers.find((item) => item === mobileNumber)) {
        (element as HTMLElement).classList.add('row-active');
        return true;
      }

      return false;
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.smsReport);
    }
  }

  onSelectAllMobileNumber(event) {
    try {
      const checkBoxElements = document.querySelectorAll(
        `#${'receivers-' + this.scheduledMessageId} .form-check-input`
      );

      checkBoxElements.forEach((checkBoxElement) => {
        if (!checkBoxElement.classList.contains('select-all')) {
          (checkBoxElement as HTMLInputElement).checked = !event;
          (checkBoxElement as HTMLInputElement).click();
        }
      });
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.smsReport);
    }
  }

  //#endregion

  onReset() {
    this.getTableData();
  }

  async checkSelectedItems() {
    if (!this.mobileNumbers?.length) return;

    let checkBoxes = await this.getAllCheckBoxesAsync();

    checkBoxes.forEach((element) => {
      const itemId = element.getAttribute('data-checkbox-id');
      if (this.mobileNumbers.includes(itemId))
        (element as HTMLInputElement).checked = true;
    });
  }

  async getAllCheckBoxesAsync(): Promise<NodeListOf<Element>> {
    try {
      return AppDomService.getElementsByQueryAsync(
        `${this.tagName} [data-checkbox-id]`
      );
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.facility);
    }
  }

  // DeliveryStatusChart
  deliveryStatusChart() {
    // this.unSubs();
    if (this.subscription) this.subscription.unsubscribe();

    this.pageConfig.loading = true;
    this.subscription = this.currentService
      .outboxMessageNumbersGetOutboxMessageDeliveryStatusStatisticsByScheduledMessageId(
        {
          body: { scheduledMessageId: this.scheduledMessageId },
        }
      )
      .pipe(this.basePipeOperators())
      .subscribe((res) => {
        try {
          if (res) {
            let data = {
              scheduledMessageId: this.scheduledMessageId,
              DeliveryStatusEnumValues: this.DeliveryStatusEnumValues,
              deliveryStatusData: res?.data,
            };

            const modalResult = this.dialogService.openModal({
              component: DeliveryStatusStats,
              data: data,
            });

            const sub = modalResult
              .afterClosed()
              .pipe(
                takeUntil(this.onDestroy$),
                tap(() => {
                  AppDomService.removeModalScrollClassFromHtml();
                })
              )
              .subscribe((res) => {
                sub.unsubscribe();
              });
          }
        } catch (error) {
          this.loggerService.logError(
            this.tagName,
            error,
            EventTypes.smsReport
          );
        }
      });
  }
}
